import { type CSSProperties, useMemo, useCallback, useState } from "react";
import { BackgroundFadeType, type BackgroundProps } from "./types";
import { styleConstructor } from "./js/styleConstructor";
import { positionMap } from "shared/src/styles/positionStyles";
import { fadePresets, patternPresets, type FadePresetType } from "./js/backgroundPresets";
import { BackgroundFade } from "./components/BackgroundFade";
import { BackgroundPattern } from "./components/BackgroundPattern";
import { useIdRef } from "shared/src/hooks/useIdRef";
import { useImageResolver } from "shared/src/hooks/useImageResolver";
import { useCuiConfig } from "shared/src/hooks/useCuiConfig";
import { useSize } from "shared/src/hooks/useSize";

export const Background = ({
  primaryColor,
  secondaryColor,
  colorMode = "primary",
  colorType = "normal",
  gradient = "horizontal",
  gradientSharpness = "blurry",
  width = "auto" as CSSProperties["width"],
  height = "auto" as CSSProperties["height"],
  fallbackPrimaryColor = "bg-primary-strong",
  fallbackSecondaryColor = "gray-dark-100",
  style = {},
  className = "",
  wrapperClasses = "",
  position = "relative",
  children,
  inset,
  fades,
  patterns,
  image,
  imageSize = "cover",
  ...rest
}: BackgroundProps) => {

  const _id = useIdRef({ str: "cui_bg_" }).current;
  const [wrapperNode, setWrapperNode] = useState<HTMLDivElement | null>(null);
  const wrapperRef = useCallback((node: HTMLDivElement | null) => { setWrapperNode(node); }, []);
  const containerSize = useSize(wrapperNode, 500, !image);
  const bgSizeTier = !containerSize?.width ? "sm" : containerSize.width > 860 ? "lg" : containerSize.width > 400 ? "md" : "sm";
  const config = useCuiConfig();
  const isFullUrl = /^https?:\/\//.test(image || '');
  const { src: imageSrc } = useImageResolver(image, config?.imagesDir, bgSizeTier, "light", !isFullUrl);
  
  const styles = styleConstructor({
    primaryColor,
    secondaryColor,
    gradient,
    colorMode,
    colorType,
    gradientSharpness,
    width,
    height,
    style,
    fallbackPrimaryColor,
    fallbackSecondaryColor,
  });

  const _position = position in positionMap ? positionMap[position] : "";

  const _Fades = useMemo(()=>{
    if(fades && Array.isArray(fades)){
      return fades.map((fade: BackgroundFadeType, i) => {
        const keyId = `${_id}-fade-${i}`;
        if ("preset" in fade && fade?.preset && fade?.preset in fadePresets) return fadePresets[fade.preset](fade as any, keyId)
        return <BackgroundFade {...fade} key={fade?.id || keyId}/>
      })
    }
  },[fades]);
  
  const _Patterns = useMemo(()=>{
    if(patterns && Array.isArray(patterns)){
      return patterns.map((pattern, i) => {
        const keyId = `${_id}-pattern-${i}`;
        if ("preset" in pattern && pattern?.preset && pattern?.preset in patternPresets) return patternPresets[pattern.preset](pattern as any, keyId)
        return <BackgroundPattern {...pattern} key={pattern?.id || keyId}/>
      })
    }
  },[patterns]);

  return (
    <div
      ref={wrapperRef}
      className={`cui__background ${_position} cui-z-0 ${wrapperClasses} ${inset ? "cui-inset-0" : ""} cui-box-border`.trim()}
      style={styles}
      {...rest}
    >
        {image && <div className="cui__background-image cui-absolute cui-inset-0 cui-bg-center cui-bg-no-repeat" style={{ backgroundImage: `url(${imageSrc})`, backgroundSize: imageSize }} />}
        {_Fades}
        {_Patterns}
        {children}
    </div>
  );
};
